'use client';

import { getToolById } from '@/lib/tools/tool-registry';

type SharePromptDetail = {
  toolId: string;
  toolName: string;
  format: string;
};

const TOOL_SHARE_CONTEXT_KEY = '2xora_active_tool_share_context';

function canUseBrowserStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

export function setActiveToolShareContext(toolId: string) {
  if (!canUseBrowserStorage()) {
    return;
  }

  const tool = getToolById(toolId);
  window.sessionStorage.setItem(
    TOOL_SHARE_CONTEXT_KEY,
    JSON.stringify({
      toolId,
      toolName: tool?.name ?? toolId,
    }),
  );
}

export function getActiveToolShareContext(): { toolId: string; toolName: string } | null {
  if (!canUseBrowserStorage()) {
    return null;
  }

  const raw = window.sessionStorage.getItem(TOOL_SHARE_CONTEXT_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as { toolId?: string; toolName?: string };
    if (!parsed.toolId) {
      return null;
    }

    return {
      toolId: parsed.toolId,
      toolName: parsed.toolName ?? getToolById(parsed.toolId)?.name ?? parsed.toolId,
    };
  } catch {
    return null;
  }
}

export function dispatchToolSharePrompt(detail: SharePromptDetail) {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent<SharePromptDetail>('2xora:tool-share-ready', { detail }));
}

export function dispatchSharePromptForActiveTool(format: string) {
  const activeTool = getActiveToolShareContext();
  if (!activeTool) {
    return;
  }

  dispatchToolSharePrompt({
    toolId: activeTool.toolId,
    toolName: activeTool.toolName,
    format,
  });
}
